import { useState, useCallback } from "react";
import { Bluelagoon, useTaskWebSocket } from "@bluelagoon";

export function useAgentChat({ addChat, integration, auth, credentials, endpoints, onIntegrationUpdated }) {
    const [chatTaskId, setChatTaskId] = useState(null);
    const [isSending, setIsSending] = useState(false);

    const handleChatUpdate = useCallback((update) => {
        const data = update.data || update;
        const feedback = data.partialFeedback || [];

        for (const entry of feedback) {
            if (entry?.type === "chat" && entry.text) addChat({ role: "ai", text: entry.text });
            if (entry?.type === "fix_complete" && entry.data && onIntegrationUpdated) onIntegrationUpdated(entry.data);
        }

        if (data.status === "COMPLETED") {
            setIsSending(false);
            setChatTaskId(null);
            const completedIntegration = data.integration || data.output?.integration;
            if (completedIntegration && onIntegrationUpdated) onIntegrationUpdated(completedIntegration);
        } else if (data.status === "FAILED" || data.status === "CANCELLED") {
            setIsSending(false);
            setChatTaskId(null);
            addChat({ role: "ai", text: "Something went wrong. Try again." });
        }
    }, [addChat, onIntegrationUpdated]);

    useTaskWebSocket({ taskId: chatTaskId, onUpdate: handleChatUpdate, enabled: !!chatTaskId });

    const sendMessage = useCallback(async (message) => {
        if (!message?.trim() || isSending) return;
        setIsSending(true);
        addChat({ role: "user", text: message });

        try {
            const fullIntegration = { ...integration, endpoints, auth };
            const res = await Bluelagoon.chatWithIntegrationAgent({ message, integration: fullIntegration, credentials });
            const body = res?.data?.data || res?.data;
            if (body?.taskId) {
                setChatTaskId(body.taskId);
            } else {
                setIsSending(false);
                addChat({ role: "ai", text: body?.message || "Failed to send message." });
            }
        } catch (err) {
            setIsSending(false);
            addChat({ role: "ai", text: `Error: ${err.message}` });
        }
    }, [isSending, addChat, integration, endpoints, auth, credentials]);

    return { sendMessage, isSending };
}
